import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { Cancel01Icon, PlayIcon } from '@hugeicons/react'

import { CATALOG_MEDIA_ASPECT_CLASS, getVideoSource } from './catalogKinds'

const STORY_MS = 5000
// Embedded players (YouTube/Vimeo) don't expose their duration without each
// vendor's JS API, so an embedded video story advances on a generous fallback
// window; the user can always tap or swipe to skip.
const VIDEO_EMBED_MS = 30000
const SWIPE_THRESHOLD = 50

const formatDuration = seconds => {
  if (!seconds || Number.isNaN(seconds)) return null
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

/**
 * Full-screen stories viewer for the premium partner catalog (handoff — overlay
 * de stories). Feature-local (not the shared DS StoriesViewer) because the
 * premium behaviour is specific: segmented auto-advancing progress, tap zones,
 * and a full-bleed video variant for `midia` items.
 *
 * - static item: 5s timer, auto-advance; hold to pause
 * - video item: progress follows the video; advances on end
 * - tap left 33% = previous, right = next; swipe left/right nav, swipe down closes
 * - keyboard: Esc closes, ←/→ navigate
 */
export const PartnerStoriesViewer = ({ items = [], initialIndex = 0, onClose, onSeen }) => {
  const [index, setIndex] = useState(initialIndex)
  const [progress, setProgress] = useState(0)
  const [videoDuration, setVideoDuration] = useState(null)

  const pausedRef = useRef(false)
  const videoRef = useRef(null)
  const dialogRef = useRef(null)
  const touchRef = useRef(null)
  // Scrollable static-item content (bug: partner-catalog-card-content-cut).
  // Only set on the non-video branch — a description too long for the
  // viewport scrolls instead of clipping.
  const contentScrollRef = useRef(null)

  const item = items[index]
  const videoSrc = getVideoSource(item)
  const isFileVideo = videoSrc?.type === 'file'
  const isEmbedVideo = !!videoSrc && !isFileVideo

  const close = useCallback(() => onClose?.(), [onClose])

  const goNext = useCallback(() => {
    setIndex(current => {
      if (current >= items.length - 1) {
        onClose?.()
        return current
      }
      return current + 1
    })
  }, [items.length, onClose])

  const goPrev = useCallback(() => {
    setIndex(current => Math.max(0, current - 1))
  }, [])

  // Keep the rAF loop pointing at the latest advance handler without re-arming.
  const goNextRef = useRef(goNext)
  useEffect(() => {
    goNextRef.current = goNext
  }, [goNext])

  // Mark the current item as seen (persisted by the caller).
  useEffect(() => {
    if (item?.id != null) onSeen?.(item.id)
  }, [item?.id, onSeen])

  // Reset scroll position when navigating between items — otherwise a
  // previous item's scrollTop can leak into the next one's DOM node.
  useEffect(() => {
    if (contentScrollRef.current) contentScrollRef.current.scrollTop = 0
  }, [index])

  // Timed auto-advance. A direct <video> drives its own progress (onTimeUpdate /
  // onEnded below), so it opts out of the timer; images use STORY_MS and
  // embedded players use the fallback window.
  useEffect(() => {
    setProgress(0)
    if (isFileVideo) return undefined

    const duration = isEmbedVideo ? VIDEO_EMBED_MS : STORY_MS
    let raf
    let last = performance.now()
    let elapsed = 0
    const tick = now => {
      const dt = now - last
      last = now
      if (!pausedRef.current) {
        elapsed += dt
        const p = Math.min(1, elapsed / duration)
        setProgress(p)
        if (p >= 1) {
          goNextRef.current()
          return
        }
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [index, isFileVideo, isEmbedVideo])

  // Esc / arrows + lock background scroll while open.
  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') close()
      else if (e.key === 'ArrowRight') goNext()
      else if (e.key === 'ArrowLeft') goPrev()
    }
    window.addEventListener('keydown', onKey)
    dialogRef.current?.focus()
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [close, goNext, goPrev])

  const setPaused = paused => {
    pausedRef.current = paused
    const el = videoRef.current
    if (!el) return
    if (paused) el.pause()
    else el.play().catch(() => {})
  }

  const onTouchStart = e => {
    const t = e.touches[0]
    touchRef.current = { x: t.clientX, y: t.clientY }
    setPaused(true)
  }

  const onTouchEnd = e => {
    setPaused(false)
    const start = touchRef.current
    if (!start) return
    const t = e.changedTouches[0]
    const dx = t.clientX - start.x
    const dy = t.clientY - start.y
    touchRef.current = null
    // Swipe-down closes, but only from the top of a scrolled description —
    // otherwise scrolling back up through long text (also a downward drag)
    // closes the viewer instead of just scrolling (partner-catalog-card-content-cut).
    const atScrollTop = (contentScrollRef.current?.scrollTop ?? 0) === 0
    if (dy > SWIPE_THRESHOLD && Math.abs(dy) > Math.abs(dx) && atScrollTop) {
      close()
    } else if (dx < -SWIPE_THRESHOLD) {
      goNext()
    } else if (dx > SWIPE_THRESHOLD) {
      goPrev()
    }
  }

  if (!items.length || !item) return null

  const durationLabel = formatDuration(videoDuration)

  const overlay = (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Catálogo — ${item.title}`}
      tabIndex={-1}
      className="fixed inset-0 z-dialog bg-[#0A0810] outline-none"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Content */}
      {videoSrc ? (
        <>
          {isFileVideo ? (
            <video
              ref={videoRef}
              src={videoSrc.url}
              poster={item.media?.file?.url || undefined}
              autoPlay
              muted
              playsInline
              preload="metadata"
              onLoadedMetadata={e => setVideoDuration(e.currentTarget.duration)}
              onTimeUpdate={e => {
                const el = e.currentTarget
                if (el.duration) setProgress(el.currentTime / el.duration)
              }}
              onEnded={goNext}
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            // Embedded player (YouTube/Vimeo) as background. pointer-events-none
            // keeps the tap zones / swipe working over it.
            <iframe
              src={videoSrc.url}
              title={item.title}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              className="pointer-events-none absolute inset-0 h-full w-full border-0 object-cover"
            />
          )}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[4] bg-[linear-gradient(to_top,rgba(6,4,10,.95)_26%,rgba(6,4,10,.7)_60%,transparent)] px-[22px] pb-[max(1.75rem,env(safe-area-inset-bottom))] pt-[130px]">
            <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1">
              <PlayIcon size={12} className="text-white" variant="solid" />
              <span className="text-2xs font-bold uppercase tracking-[.05em] text-white">
                Vídeo{durationLabel ? ` · ${durationLabel}` : ''}
              </span>
            </div>
            <h2 className="m-0 text-[23px] font-extrabold leading-tight text-white">{item.title}</h2>
            {item.description && (
              <p className="mt-2 mb-0 text-[14.5px] leading-relaxed text-white/[.78]">
                {item.description}
              </p>
            )}
            {item.link && (
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="pointer-events-auto mt-4 flex h-[50px] w-full items-center justify-center rounded-full bg-white text-[15px] font-bold text-gray-900 no-underline active:scale-[.98]"
              >
                Saiba mais ›
              </a>
            )}
          </div>
        </>
      ) : (
        // overflow-y-auto + min-h-full (not h-full) on the inner flex box: short
        // content still centers like before, but content taller than the
        // viewport — a wide-ratio image plus a long description — scrolls
        // instead of being clipped with no way to reach the rest (bug:
        // partner-catalog-card-content-cut).
        <div ref={contentScrollRef} className="absolute inset-0 overflow-y-auto">
          <div className="flex min-h-full flex-col justify-center px-[26px] pb-[max(2.5rem,env(safe-area-inset-bottom))] pt-[max(4.375rem,calc(4.375rem+env(safe-area-inset-top)))] text-center">
            {item.media?.file?.url ? (
              <img
                src={item.media.file.url}
                alt={item.title}
                className={`w-full rounded-[20px] object-cover ${CATALOG_MEDIA_ASPECT_CLASS}`}
              />
            ) : (
              <div className={`w-full rounded-[20px] bg-white/5 ${CATALOG_MEDIA_ASPECT_CLASS}`} />
            )}
            <h2 className="mx-auto mt-[22px] max-w-sm text-2xl font-extrabold leading-tight text-white">
              {item.title}
            </h2>
            {item.description && (
              // relative + z-[3] lifts the text above the full-height prev/next
              // tap zones (z-[2]) so a touch here scrolls instead of navigating
              // (partner-catalog-card-content-cut) — image/title stay under the
              // tap zones on purpose, tapping them still advances the story.
              <p className="relative z-[3] mx-auto mt-2.5 mb-0 max-w-sm text-[14.5px] leading-relaxed text-white/[.72]">
                {item.description}
              </p>
            )}
            {item.link && (
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex h-12 items-center justify-center self-center rounded-full bg-white px-[30px] text-[15px] font-bold text-gray-900 no-underline active:scale-[.98]"
              >
                Saiba mais
              </a>
            )}
          </div>
        </div>
      )}

      {/* Tap zones */}
      <button
        type="button"
        aria-label="Anterior"
        onPointerDown={() => setPaused(true)}
        onPointerUp={() => setPaused(false)}
        onClick={goPrev}
        className="absolute bottom-0 left-0 top-16 z-[2] w-1/3 cursor-default bg-transparent"
      />
      <button
        type="button"
        aria-label="Próximo"
        onPointerDown={() => setPaused(true)}
        onPointerUp={() => setPaused(false)}
        onClick={goNext}
        className="absolute bottom-0 right-0 top-16 z-[2] w-1/2 cursor-default bg-transparent"
      />

      {/* Header: segmented progress + close */}
      <div className="absolute inset-x-0 top-0 z-[6] px-3.5 pt-[max(0.875rem,env(safe-area-inset-top))]">
        <div className="flex gap-1" aria-hidden>
          {items.map((it, i) => (
            <div key={it.id} className="h-[3px] flex-1 overflow-hidden rounded-sm bg-white/[.28]">
              <div
                className="h-full rounded-sm bg-white"
                style={{ width: i < index ? '100%' : i === index ? `${progress * 100}%` : '0%' }}
              />
            </div>
          ))}
        </div>
        <div className="mt-2.5 flex justify-end">
          <button
            type="button"
            onClick={close}
            aria-label="Fechar"
            className="grid h-9 w-9 place-items-center rounded-full bg-white/[.22] text-white active:scale-95"
          >
            <Cancel01Icon size={20} variant="stroke" />
          </button>
        </div>
        <span className="sr-only" aria-live="polite">{`${index + 1} de ${items.length}`}</span>
      </div>
    </div>
  )

  if (typeof document === 'undefined') return null

  return createPortal(overlay, document.body)
}
